import {
  ApprovalConfirmMode,
  ApprovalEditedEntries,
  ApprovalExclusion,
  ApprovalExclusionKind,
  ApprovalHoursSplit,
  ApprovalSelectedMember,
  ApprovalSelectedProject,
  ApprovalSelection,
  ApprovalSelectionCounts,
  ApprovalSelectionLevel,
  ApprovalTimeEntry,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';

const EMPTY_HOURS_SPLIT: ApprovalHoursSplit = { invoice: 0, bill: 0 };

/** Zero counts — also the stand-in for "no selection yet" (the confirm modal while closed). */
export const EMPTY_SELECTION_COUNTS: ApprovalSelectionCounts = {
  approveCount: 0,
  unapproveCount: 0,
  approveHours: EMPTY_HOURS_SPLIT,
  unapproveHours: EMPTY_HOURS_SPLIT,
};

const addSplits = (a: ApprovalHoursSplit, b: ApprovalHoursSplit): ApprovalHoursSplit => ({
  invoice: a.invoice + b.invoice,
  bill: a.bill + b.bill,
});

const subtractSplits = (a: ApprovalHoursSplit, b: ApprovalHoursSplit): ApprovalHoursSplit => ({
  invoice: Math.max(0, a.invoice - b.invoice),
  bill: Math.max(0, a.bill - b.bill),
});

const countsFromEntryExclusion = (
  exclusion: Extract<ApprovalExclusion, { kind: ApprovalExclusionKind.Entry }>
): ApprovalSelectionCounts => {
  const isPending = exclusion.status === TimeEntryStatus.SUBMITTED;
  const isApproved = exclusion.status === TimeEntryStatus.APPROVED;
  // Both baselines are approved hours, so both sides of the snapshot carve out the same figures —
  // for a pending entry that is what an approve WOULD have written for it.
  const approvedHours: ApprovalHoursSplit = {
    invoice: exclusion.approvedForInvoiceHours,
    bill: exclusion.approvedForBillHours,
  };
  return {
    approveCount: isPending ? 1 : 0,
    unapproveCount: isApproved ? 1 : 0,
    approveHours: isPending ? approvedHours : EMPTY_HOURS_SPLIT,
    unapproveHours: isApproved ? approvedHours : EMPTY_HOURS_SPLIT,
  };
};

const countsFromMemberExclusion = (
  exclusion: Extract<ApprovalExclusion, { kind: ApprovalExclusionKind.Member }>
): ApprovalSelectionCounts => ({
  approveCount: exclusion.pendingWorklogs,
  unapproveCount: exclusion.approvedWorklogs,
  approveHours: {
    invoice: exclusion.pendingApprovedForInvoiceHours,
    bill: exclusion.pendingApprovedForBillHours,
  },
  unapproveHours: {
    invoice: exclusion.approvedForInvoiceHours,
    bill: exclusion.approvedForBillHours,
  },
});

const addCounts = (
  a: ApprovalSelectionCounts,
  b: ApprovalSelectionCounts
): ApprovalSelectionCounts => ({
  approveCount: a.approveCount + b.approveCount,
  unapproveCount: a.unapproveCount + b.unapproveCount,
  approveHours: addSplits(a.approveHours, b.approveHours),
  unapproveHours: addSplits(a.unapproveHours, b.unapproveHours),
});

const subtractCounts = (
  a: ApprovalSelectionCounts,
  b: ApprovalSelectionCounts
): ApprovalSelectionCounts => ({
  approveCount: Math.max(0, a.approveCount - b.approveCount),
  unapproveCount: Math.max(0, a.unapproveCount - b.unapproveCount),
  approveHours: subtractSplits(a.approveHours, b.approveHours),
  unapproveHours: subtractSplits(a.unapproveHours, b.unapproveHours),
});

/** Sums the counts an exclusion list removes from its covering selection. */
const sumExclusionCounts = (exclusions: ApprovalExclusion[]): ApprovalSelectionCounts =>
  exclusions.reduce(
    (acc, exclusion) =>
      addCounts(
        acc,
        exclusion.kind === ApprovalExclusionKind.Entry
          ? countsFromEntryExclusion(exclusion)
          : countsFromMemberExclusion(exclusion)
      ),
    EMPTY_SELECTION_COUNTS
  );

/**
 * Hours an aggregate selection stops approving because some of its entries carry a pending
 * inline edit. The aggregate's `pendingApprovedFor*Hours` counts every pending entry at its
 * SAVED approved value, but an edited entry will be approved at the edited one — so the
 * difference is subtracted (a negative delta, i.e. an edit above the saved value, adds).
 * Entry COUNTS are untouched: an edited entry is still approved, just for other hours.
 *
 * Each side gets its own delta from its own baseline: an invoice-only edit leaves the bill
 * baseline in place and contributes nothing to the bill figure.
 */
const countsFromEditedEntries = (
  editedEntries: ApprovalEditedEntries
): ApprovalSelectionCounts => ({
  ...EMPTY_SELECTION_COUNTS,
  approveHours: editedEntries.reduce(
    (delta, { entry, baseline }) =>
      addSplits(delta, {
        invoice: baseline.invoice - entry.approvedForInvoiceHours,
        bill: baseline.bill - entry.approvedForBillHours,
      }),
    EMPTY_HOURS_SPLIT
  ),
});

/** Aggregate-row counts minus what its exclusions and pending edits carve out. */
const subtractExclusionsAndEdits = (
  base: ApprovalSelectionCounts,
  exclusions: ApprovalExclusion[],
  editedEntries: ApprovalEditedEntries
): ApprovalSelectionCounts =>
  subtractCounts(
    subtractCounts(base, sumExclusionCounts(exclusions)),
    countsFromEditedEntries(editedEntries)
  );

/** Counts of one selected project card (its aggregates minus its exclusions and edits). */
export const getSelectedProjectCounts = ({
  project,
  exclusions,
  editedEntries,
}: ApprovalSelectedProject): ApprovalSelectionCounts =>
  subtractExclusionsAndEdits(
    {
      approveCount: project.pendingWorklogs,
      unapproveCount: project.approvedWorklogs,
      approveHours: {
        invoice: project.pendingApprovedForInvoiceHours,
        bill: project.pendingApprovedForBillHours,
      },
      unapproveHours: {
        invoice: project.approvedForInvoiceHours,
        bill: project.approvedForBillHours,
      },
    },
    exclusions,
    editedEntries
  );

/** Counts of one selected member card (its aggregates minus its exclusions and edits). */
export const getSelectedMemberCounts = ({
  member,
  exclusions,
  editedEntries,
}: ApprovalSelectedMember): ApprovalSelectionCounts =>
  subtractExclusionsAndEdits(
    {
      approveCount: member.pendingWorklogs,
      unapproveCount: member.approvedWorklogs,
      approveHours: {
        invoice: member.pendingApprovedForInvoiceHours,
        bill: member.pendingApprovedForBillHours,
      },
      unapproveHours: {
        invoice: member.approvedForInvoiceHours,
        bill: member.approvedForBillHours,
      },
    },
    exclusions,
    editedEntries
  );

const countsFromEntry = (entry: ApprovalTimeEntry): ApprovalSelectionCounts => {
  if (entry.status === TimeEntryStatus.SUBMITTED) {
    // The values the approve will actually write: a pending entry's approved figures are its
    // overrides (or its tracked hours) and already carry the pending inline edit, merged in by
    // the selection.
    return {
      ...EMPTY_SELECTION_COUNTS,
      approveCount: 1,
      approveHours: { invoice: entry.approvedForInvoiceHours, bill: entry.approvedForBillHours },
    };
  }
  if (entry.status === TimeEntryStatus.APPROVED) {
    return {
      ...EMPTY_SELECTION_COUNTS,
      unapproveCount: 1,
      unapproveHours: { invoice: entry.approvedForInvoiceHours, bill: entry.approvedForBillHours },
    };
  }
  return EMPTY_SELECTION_COUNTS;
};

/**
 * Splits a selection into approvable vs unapprovable counts + hours — drives the footer
 * button counts, the confirm modal, and the success toasts. Worklog level reads each
 * entry's live status (so pending inline edits are reflected); project/member level uses
 * the backend aggregates minus recorded exclusions and the deltas of the pending inline
 * edits it covers (children may not even be loaded).
 */
export const getApprovalSelectionCounts = (
  selection: ApprovalSelection
): ApprovalSelectionCounts => {
  switch (selection.level) {
    case ApprovalSelectionLevel.Projects:
      return selection.projects.reduce(
        (acc, selectedProject) => addCounts(acc, getSelectedProjectCounts(selectedProject)),
        EMPTY_SELECTION_COUNTS
      );
    case ApprovalSelectionLevel.Members:
      return selection.members.reduce(
        (acc, selectedMember) => addCounts(acc, getSelectedMemberCounts(selectedMember)),
        EMPTY_SELECTION_COUNTS
      );
    case ApprovalSelectionLevel.Worklogs:
      return selection.entries.reduce(
        (acc, entry) => addCounts(acc, countsFromEntry(entry)),
        EMPTY_SELECTION_COUNTS
      );
  }
};

export const getApprovalActionTotals = (
  counts: ApprovalSelectionCounts,
  mode: ApprovalConfirmMode
): { count: number; hours: ApprovalHoursSplit } =>
  mode === ApprovalConfirmMode.Approve
    ? { count: counts.approveCount, hours: counts.approveHours }
    : { count: counts.unapproveCount, hours: counts.unapproveHours };

/** Number of entries the given action (approve/unapprove) would actually change. */
export const getApprovalSelectionCountForMode = (
  selection: ApprovalSelection,
  mode: ApprovalConfirmMode
): number => {
  const counts = getApprovalSelectionCounts(selection);
  return mode === ApprovalConfirmMode.Approve ? counts.approveCount : counts.unapproveCount;
};
